import {
  MARKUP_SETTINGS_VERSION,
  ROUNDING_VALUES,
  type MarginRule,
  type MarkupSettingsSnapshot,
  type PriceType,
  type Rounding,
} from "../../../models/markupSettings";

/**
 * Приведение записи наценок к рабочему виду.
 *
 * Запись приходит из двух мест — с сервера и из прежнего localStorage — и в обоих
 * ей нельзя верить на слово: старая сборка могла записать неполный объект, ключ
 * могли поправить руками, JSON мог побиться. Поэтому каждое поле проверяется по
 * отдельности и при сомнении заменяется безопасным значением, а не роняет загрузку.
 */

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function toFiniteNumber(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function normalizeRounding(value: unknown): Rounding {
  return ROUNDING_VALUES.find((candidate) => candidate === value) ?? "none";
}

function normalizePriceTypes(value: unknown): PriceType[] {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is UnknownRecord => isRecord(item) && isNonEmptyString(item.id))
    .map((item) => ({
      id: item.id as string,
      name: typeof item.name === "string" ? item.name : "",
    }));
}

function normalizeRule(item: UnknownRecord): MarginRule {
  return {
    id: item.id as string,
    typeId: item.typeId as string,
    pluginId: toFiniteNumber(item.pluginId, 0),
    threshold: toFiniteNumber(item.threshold, 0),
    percent: toFiniteNumber(item.percent, 0),
    enabled: item.enabled !== false,
  };
}

/** Правило без своего типа цен в интерфейсе недостижимо — такие отбрасываем. */
function normalizeRules(value: unknown, priceTypes: PriceType[]): MarginRule[] {
  if (!Array.isArray(value)) return [];

  const knownTypeIds = new Set(priceTypes.map((type) => type.id));

  return value
    .filter(
      (item): item is UnknownRecord =>
        isRecord(item) && isNonEmptyString(item.id) && isNonEmptyString(item.typeId) && knownTypeIds.has(item.typeId),
    )
    .map(normalizeRule);
}

function normalizeStringListRecord(value: unknown): Record<string, string[]> {
  if (!isRecord(value)) return {};

  const result: Record<string, string[]> = {};
  for (const [key, list] of Object.entries(value)) {
    if (Array.isArray(list)) result[key] = list.filter(isNonEmptyString);
  }
  return result;
}

function normalizePrimaryPriceTypeId(value: unknown): Record<string, string | null> {
  if (!isRecord(value)) return {};

  const result: Record<string, string | null> = {};
  for (const [key, typeId] of Object.entries(value)) {
    result[key] = isNonEmptyString(typeId) ? typeId : null;
  }
  return result;
}

/**
 * Разбор записи из любого источника. `null` — когда это вообще не запись наценок
 * (не объект): такое считается отсутствием данных, а не пустыми настройками.
 */
export function normalizeMarkupSnapshot(raw: unknown): MarkupSettingsSnapshot | null {
  if (!isRecord(raw)) return null;

  const priceTypes = normalizePriceTypes(raw.priceTypes);

  return {
    version: MARKUP_SETTINGS_VERSION,
    rounding: normalizeRounding(raw.rounding),
    priceTypes,
    rules: normalizeRules(raw.rules, priceTypes),
    costTypeMappings: normalizeStringListRecord(raw.costTypeMappings),
    primaryPriceTypeId: normalizePrimaryPriceTypeId(raw.primaryPriceTypeId),
    selectedPriceTypeIds: normalizeStringListRecord(raw.selectedPriceTypeIds),
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : "",
  };
}

export function createEmptyMarkupSnapshot(): MarkupSettingsSnapshot {
  return {
    version: MARKUP_SETTINGS_VERSION,
    rounding: "none",
    priceTypes: [],
    rules: [],
    costTypeMappings: {},
    primaryPriceTypeId: {},
    selectedPriceTypeIds: {},
    updatedAt: "",
  };
}

/**
 * Есть ли в записи то, ради чего её стоит переносить на сервер. Пустые настройки
 * из браузера на сервер не заливаем: запись появится с первой настоящей правкой.
 */
export function hasMarkupData(snapshot: MarkupSettingsSnapshot): boolean {
  return snapshot.priceTypes.length > 0 || snapshot.rounding !== "none";
}
